/**
 * USD bank-account waitlist — /naytive-banking-waitlist
 *
 * Founders join from the user panel's banking surface ("USD accounts coming
 * soon → join the waitlist"). This surfaces those signups so ops can move
 * each through waitlisted → invited → opened / declined, and jot notes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { naytiveAPI } from '../services/api';

const STATUSES = ['waitlisted', 'invited', 'opened', 'declined'];
const STATUS_COLORS = {
  waitlisted: { bg: '#FEF3C7', fg: '#92400E' },
  invited: { bg: '#DBEAFE', fg: '#1D4ED8' },
  opened: { bg: '#D1FAE5', fg: '#065F46' },
  declined: { bg: '#FEE2E2', fg: '#991B1B' },
};

export default function NaytiveBankingWaitlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [toast, setToast] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2400); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listBankingWaitlist(
        filter === 'all' ? {} : { status: filter },
      );
      setItems(r.data?.items || []);
    } catch (e) {
      console.error('[bankingWaitlist] load failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await naytiveAPI.updateBankingWaitlist(id, { status });
      await load();
    } catch (e) {
      flash(e.response?.data?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const addNote = async (id) => {
    const note = (noteDraft[id] || '').trim();
    if (!note) return;
    setBusyId(id);
    try {
      await naytiveAPI.updateBankingWaitlist(id, { adminNotes: note });
      setNoteDraft((d) => ({ ...d, [id]: '' }));
      await load();
    } catch (e) {
      flash(e.response?.data?.message || 'Note failed');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of STATUSES) c[s] = items.filter((i) => i.status === s).length;
    return c;
  }, [items]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' }}>
                USD bank-account waitlist
              </h1>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
                Founders who joined the "USD accounts coming soon" waitlist from the app.
              </div>
            </div>
            <button style={btnGhost} onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* Filter chips */}
          <div style={{ display: 'flex', gap: 6, margin: '16px 0', flexWrap: 'wrap' }}>
            {['all', ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                style={{ ...chipBtn, ...(filter === s ? chipActive : null) }}
              >
                {s === 'all' ? 'All' : s}
                <span style={{ opacity: 0.6, marginLeft: 6 }}>{counts[s] ?? 0}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div style={emptyBox}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={emptyBox}>No waitlist signups{filter !== 'all' ? ` in "${filter}"` : ''} yet.</div>
          ) : (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#6B7280' }}>
                    <th style={th}>Founder</th>
                    <th style={th}>Entity</th>
                    <th style={th}>Joined</th>
                    <th style={th}>Status</th>
                    <th style={th}>Notes / actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const pal = STATUS_COLORS[r.status] || {};
                    return (
                      <tr key={r.id} style={{ borderTop: '1px solid #F3F4F6', verticalAlign: 'top' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>
                            {r.user_name || r.account_email || r.email || `user #${r.user_id}`}
                          </div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>{r.email || r.account_email}</div>
                        </td>
                        <td style={td}>{r.entity_name || (r.entity_id ? `#${r.entity_id}` : '—')}</td>
                        <td style={td}>{fmt(r.created_at)}</td>
                        <td style={td}>
                          <span style={{ ...statusPill, background: pal.bg || '#F3F4F6', color: pal.fg || '#374151' }}>
                            {r.status}
                          </span>
                          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                            {STATUSES.filter((s) => s !== r.status).map((s) => (
                              <button
                                key={s}
                                disabled={busyId === r.id}
                                onClick={() => setStatus(r.id, s)}
                                style={miniBtn}
                                title={`Mark ${s}`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td style={{ ...td, minWidth: 240 }}>
                          {r.notes && (
                            <pre style={notesBox}>{r.notes}</pre>
                          )}
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <input
                              value={noteDraft[r.id] || ''}
                              onChange={(e) => setNoteDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') addNote(r.id); }}
                              placeholder="Add a note…"
                              style={{ flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 12.5 }}
                            />
                            <button
                              style={btnGhost}
                              disabled={busyId === r.id || !(noteDraft[r.id] || '').trim()}
                              onClick={() => addNote(r.id)}
                            >
                              Add
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
}

function fmt(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const btnGhost = { padding: '7px 13px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const chipBtn = { padding: '6px 13px', border: '1px solid #E5E7EB', borderRadius: 999, background: '#fff', color: '#6B7280', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' };
const chipActive = { background: '#EEF2FF', color: '#2540D9', borderColor: '#C7D2FE' };
const th = { padding: '11px 14px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' };
const td = { padding: '12px 14px', color: '#374151' };
const statusPill = { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize' };
const miniBtn = { fontSize: 11, padding: '3px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', color: '#6B7280', cursor: 'pointer', textTransform: 'capitalize' };
const notesBox = { whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12, color: '#4B5563', background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: 7, padding: '6px 9px', margin: 0 };
const emptyBox = { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', marginTop: 4 };
const toastStyle = { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 };
